import { IN_NATIVE, NATIVE_AUTH_REDIRECT } from '../native/shell.ts';
import { ALL_AUTH_METHODS, PROVIDERS, type AuthMethods } from './types.ts';

/**
 * What the build knows about the account service. All of it is public (the anonymous key of a
 * Supabase project is meant to be in the page; what protects the data is row level security, not
 * the key), and all of it is optional: without a URL and a key the app has no cloud account.
 */

export interface AccountConfig {
  url: string;
  anonKey: string;
  /** The Cloudflare Turnstile site key, if the project asks for a captcha. */
  turnstileSiteKey: string | null;
  /** Where Auth sends the browser back to after a provider or an e-mail link. */
  redirectTo: string;
  /** Which ways of getting in work with this project. */
  methods: AuthMethods;
}

interface Env {
  VITE_SUPABASE_URL?: string | undefined;
  VITE_SUPABASE_ANON_KEY?: string | undefined;
  VITE_TURNSTILE_SITE_KEY?: string | undefined;
  VITE_AUTH_REDIRECT_URL?: string | undefined;
  VITE_AUTH_METHODS?: string | undefined;
}

const blank = (v: string | undefined): string | null => {
  const t = v?.trim();
  return t ? t : null;
};

/**
 * The ways of getting in that the build switches on, from `VITE_AUTH_METHODS`: a list of `password`, `code`,
 * `google`, `discord` and `apple` (`guest` and anything else are ignored: the guest way is always there).
 * Not set: all of them (development, the stand-in backend and the tests). Set to `guest`, or to anything with
 * none of the names: only the guest way.
 */
export function readAuthMethods(value: string | undefined): AuthMethods {
  if (value === undefined || value.trim() === '') return ALL_AUTH_METHODS;
  const names = new Set(value.toLowerCase().split(/[^a-z]+/));
  return {
    password: names.has('password'),
    code: names.has('code'),
    providers: PROVIDERS.filter((p) => names.has(p)),
  };
}

/** The configuration, or null when there is no project to talk to. */
export function readAccountConfig(
  env: Env = import.meta.env,
  origin: string = typeof location === 'undefined' ? 'http://localhost:5173' : location.origin,
): AccountConfig | null {
  const url = blank(env.VITE_SUPABASE_URL);
  const anonKey = blank(env.VITE_SUPABASE_ANON_KEY);
  if (!url || !anonKey) return null;
  return {
    url,
    anonKey,
    turnstileSiteKey: blank(env.VITE_TURNSTILE_SITE_KEY),
    methods: readAuthMethods(env.VITE_AUTH_METHODS),
    // A native app has no address of its own on the web: the way back is a link of the app.
    redirectTo:
      blank(env.VITE_AUTH_REDIRECT_URL) ??
      (IN_NATIVE ? NATIVE_AUTH_REDIRECT : `${origin}/auth/callback`),
  };
}
